import { React, useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import "./professional-consult-style.css";
import RatingDialog from "../../dialog/rating-feedback/rating-dialog";
import ConfirmDialog from "../../dialog/confirm/confirm-dialog";
import Loading from "../../dialog/loading/loading";
import ConsultService from "../../../service/consult-service";
import SiteFooter from "../../custom/site-footer/site-footer";
import TeacherInfo from "../../custom/teacher-info/teacher-info";
import useIsMobile from "../../../hooks/useIsMobile";

//planID → 商品卡圖片
const getProductImage = (productID) => {
  if (productID === "digital_flow") return "/images/bg-flow-book.png";
  if (productID === "digital_love") return "/images/bg-love-match.png";
  if (productID === "digital_family") return "/images/bg-family-match.png";
  if (productID === "digital_qimen") return "/images/bg-qimen-dunjia.png";
  return "/images/bg-personal-question.png";
};

const ConsultPageComponent = ({ setShowServiceMenu, setShowPersonalMenu }) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const chatRef = useRef(null);
  const inputRef = useRef(null);

  //訂單編號（"0" = 個人命盤附帶的諮詢）
  //  優先取 URL query（重新整理仍保留），其次 router state，最後預設 "0"
  const orderID = String(
    searchParams.get("order") || location.state?.orderID || "0",
  );

  const [consult, setConsult] = useState(null);
  //諮詢卡上顯示的人員（個人/流年 1 筆、愛情/親子 2 筆）
  const [consultPeople, setConsultPeople] = useState([]);
  //商品客製欄位（如奇門遁甲：[{label:"姓名"},{label:"盤別"},{label:"事件"}]）
  //  有值時覆寫掉預設的「姓名/性別/出生日期/出生時辰」固定欄位
  const [customLabels, setCustomLabels] = useState(null);
  const [followupCost, setFollowupCost] = useState(0);
  const [userAPoint, setUserAPoint] = useState(0);
  //下一則訊息是否免費（後端算好、前端直接讀，不用重算）
  const [nextIsFree, setNextIsFree] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [question, setQuestion] = useState("");
  const [replyTo, setReplyTo] = useState(""); //撰寫中引用的訊息內容
  const [sending, setSending] = useState(false);

  const [showRating, setShowRating] = useState(false);
  const [ratingTarget, setRatingTarget] = useState(null); //被評分的訊息
  const [showConfirm, setShowConfirm] = useState(false);
  const [showNoPoint, setShowNoPoint] = useState(false); //A-Point不足提示

  //載入諮詢串
  const loadThread = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await ConsultService.getThread(orderID);
      const d = res.data.data;
      setConsult(d.consult);
      setConsultPeople(Array.isArray(d.people) ? d.people : []);
      setCustomLabels(
        Array.isArray(d.customLabels) && d.customLabels.length > 0
          ? d.customLabels
          : null,
      );
      setFollowupCost(d.cost);
      setUserAPoint(d.balanceAfter);
      setNextIsFree(!!d.nextIsFree);
    } catch (e) {
      setError(e?.response?.data?.message || "讀取諮詢失敗");
    } finally {
      setLoading(false);
    }
  }, [orderID]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  const messages = consult?.messages || [];
  //後端算好的下一則免費旗標（CN-1 / CN-2 都會回）
  const canFree = nextIsFree;

  //聊天區捲到底
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages.length]);

  //點「回覆」→ 帶入引用 + 捲到輸入框
  const handleReply = (content) => {
    setReplyTo(content);
    requestAnimationFrame(() => {
      inputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  //送出諮詢
  const submitQuestion = async () => {
    setShowConfirm(false);
    if (!question.trim() || sending) return;
    setSending(true);
    try {
      const res = await ConsultService.sendMessage({
        consultID: consult.consultID,
        content: question.trim(),
        replyTo,
      });
      const d = res.data.data || {};
      //點數不足：後端只回 resultCode="-1"，沒有 consult 更新，但仍回 balanceAfter
      //  跳「A-Point 不足」提示，維持原輸入內容不清空，讓使用者去儲值後回來直接送
      if (d.resultCode === "-1") {
        if (typeof d.balanceAfter === "number") setUserAPoint(d.balanceAfter);
        setShowNoPoint(true);
        return;
      }
      //resultCode === "1"：成功送出
      setConsult(d.consult);
      setUserAPoint(d.balanceAfter);
      setNextIsFree(!!d.nextIsFree);
      setQuestion("");
      setReplyTo("");
    } catch (e) {
      alert(e?.response?.data?.message || "送出失敗");
    } finally {
      setSending(false);
    }
  };

  //點「送出諮詢」→ 付費追問先檢查 A-Point是否足夠，再二次確認
  const handleSubmitClick = () => {
    if (!question.trim() || sending) return;
    //付費追問且 A-Point不足 → 跳提示，導去點數管理
    if (!canFree && userAPoint < followupCost) {
      setShowNoPoint(true);
      return;
    }
    setShowConfirm(true);
  };

  //開啟評分
  const openRating = (message) => {
    setRatingTarget(message);
    setShowRating(true);
  };

  //評分送出（含文字回饋）
  const handleRate = async (stars, feedback) => {
    if (!ratingTarget) return;
    try {
      const res = await ConsultService.rate({
        consultID: consult.consultID,
        messageID: ratingTarget.messageID,
        stars,
        feedback: feedback || "",
      });
      setConsult(res.data.data.consult);
    } catch (e) {
      alert(e?.response?.data?.message || "評分失敗");
    }
  };

  //商品卡：愛情/親子合盤有 2 筆 people，個人/流年 1 筆
  //  2 筆時用 " , " 串接（跟我的書房卡片同風格）
  const renderProductCard = () => {
    const joinField = (key) =>
      consultPeople.length === 0
        ? "—"
        : consultPeople.map((p) => p?.[key] || "—").join(" , ");

    return (
      <div className="product-card">
        <img
          className="product-holder"
          src={getProductImage(consult?.productID)}
          alt=""
        />
        <div className="order-detail">
          <span className="product-name">
            {consult?.productName || "專業諮詢"}
          </span>
          <div className="order-line"></div>
          <div className="order-info">
            <div className="item-info">
              <span className="item-label">訂單編號</span>
              <span>{orderID === "0" ? "個人命盤" : orderID}</span>
            </div>
            {customLabels ? (
              //奇門遁甲：用客製欄位覆寫預設四項
              customLabels.map((it, i) => (
                <div className="item-info" key={i}>
                  <span className="item-label">{it.label}</span>
                  <span>{it.value}</span>
                </div>
              ))
            ) : (
              <>
                <div className="item-info">
                  <span className="item-label">姓名</span>
                  <span>{joinField("name")}</span>
                </div>
                <div className="item-info">
                  <span className="item-label">性別</span>
                  <span>{joinField("gender")}</span>
                </div>
                <div className="item-info">
                  <span className="item-label">出生日期</span>
                  <span>{joinField("birth")}</span>
                </div>
                <div className="item-info">
                  <span className="item-label">出生時辰/真太陽時</span>
                  <span>{joinField("birthTime")}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  //聊天訊息區
  const renderChat = () => {
    if (messages.length === 0) return null;
    return (
      <div className="consult-chat" ref={chatRef}>
        {messages.map((message) => (
          <div className="message" key={message.messageID}>
            {message.sender === "user" ? (
              <div className="customer">
                <div
                  className="action-c"
                  onClick={() => handleReply(message.content)}
                >
                  <img src="../images/ic-reply.png" alt="" />
                  <span>回覆</span>
                </div>
                <div className="msg-c">
                  {message.replyTo && (
                    <div className="reply-area">
                      <span className="reply">{`回覆：${message.replyTo}`}</span>
                      <div className="msg-line"></div>
                    </div>
                  )}
                  <span>{message.content}</span>
                </div>
              </div>
            ) : (
              <div className="teacher">
                <div className="msg-area">
                  <div className="msg-t">
                    {message.replyTo && (
                      <div className="reply-area">
                        <span className="reply">{`回覆：${message.replyTo}`}</span>
                        <div className="msg-line"></div>
                      </div>
                    )}
                    <span>{message.content}</span>
                  </div>

                  {Number(message.stars) > 0 && (
                    <div className="stars">
                      {Array.from({ length: message.stars }).map((_, i) => (
                        <img
                          key={`f${i}`}
                          src="../images/ic-gold-fill-star.png"
                          alt=""
                        />
                      ))}
                      {Array.from({ length: 5 - message.stars }).map((_, i) => (
                        <img
                          key={`e${i}`}
                          src="../images/ic-gold-empty-star.png"
                          alt=""
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div
                  className={`action-t ${
                    message.stars > 0 ? "action-t-recoup" : ""
                  }`}
                  onClick={() => handleReply(message.content)}
                >
                  <img src="../images/ic-reply.png" alt="" />
                  <span>回覆</span>
                </div>

                {Number(message.stars) === 0 && (
                  <div className="action-t" onClick={() => openRating(message)}>
                    <img src="../images/ic-default-star.png" alt="" />
                    <span>評分</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  //輸入區 + 送出按鈕
  const renderSubmit = () => (
    <>
      <div className="submit-card" ref={inputRef}>
        {replyTo && (
          <>
            <div className="reply-msg">
              <span>{`回覆：${replyTo}`}</span>
              <img
                src="../images/ic-default-close.png"
                alt=""
                onClick={() => setReplyTo("")}
              />
            </div>
            <div className="submit-line"></div>
          </>
        )}
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="您可以根據此訂單的內容提出諮詢。若詢問其他不相關之問題，老師可能會斟酌回覆。
老師回覆後，我們會寄信件至您的電子信箱通知您。"
        ></textarea>
      </div>

      <span
        className={`submit ${question.trim().length > 0 ? "active-submit" : ""}`}
        onClick={handleSubmitClick}
      >
        {`送出諮詢${canFree ? "（免費乙次）" : `（${followupCost} A-Point）`}`}
      </span>
    </>
  );

  //聊天主體（web / mobile 共用）
  const renderContent = () => {
    if (loading) {
      return <Loading />;
    }
    if (error) {
      return <div className="consult-title">{error}</div>;
    }
    return (
      <>
        <TeacherInfo />
        {renderProductCard()}
        <div className="consult-title">{`- ${
          consult?.productName || "專業"
        } 諮詢內容 -`}</div>
        {renderChat()}
        {renderSubmit()}
      </>
    );
  };

  return (
    <div className="consult-page">
      {/* 電腦版佈局 */}
      {!isMobile && (
        <div
          className="web-main"
          onMouseEnter={() => {
            setShowServiceMenu(false);
            setShowPersonalMenu(false);
          }}
        >
          <div className="content">
            <div className="title">
              <img src="../images/ic-consult.png" alt="" />
              <span>專業諮詢</span>
            </div>
            {renderContent()}
          </div>

          <div className="divide"></div>

          {/*隱私政策與聯絡官方*/}
          <SiteFooter />
        </div>
      )}

      {/* 移動裝置佈局 */}
      {isMobile && (
        <div className="mobile-main">
          <div className="content">
            <div className="title">
              <img src="../images/ic-consult.png" alt="" />
              <span>專業諮詢</span>
            </div>
            {renderContent()}
          </div>

          <div className="divide"></div>

          <SiteFooter />
        </div>
      )}

      {/* 評分 dialog */}
      {showRating && (
        <RatingDialog
          open={showRating}
          onClose={() => setShowRating(false)}
          onSuccess={(stars, feedback) => handleRate(stars, feedback)}
        />
      )}

      {/* 送出確認 dialog */}
      {showConfirm && (
        <ConfirmDialog
          open={showConfirm}
          onClose={() => setShowConfirm(false)}
          onSuccess={submitQuestion}
          title="確定送出諮詢？"
          message={
            (canFree
              ? "此次為免費的諮詢機會"
              : `此次諮詢將扣除 ${followupCost} A-Point（目前 ${userAPoint} A-Point）`) +
            "<br/>請確認諮詢內容無誤再行送出"
          }
          confirmTxt="確認送出"
          image="/images/ic-send.png"
        />
      )}

      {/* A-Point不足 dialog → 導向點數管理 */}
      {showNoPoint && (
        <ConfirmDialog
          open={showNoPoint}
          onClose={() => setShowNoPoint(false)}
          onSuccess={() => navigate("/point")}
          title="A-Point 點數不足"
          message={
            `此次諮詢需 ${followupCost} A-Point，您目前只有 ${userAPoint} A-Point` +
            "<br/>請前往點數管理進行儲值"
          }
          confirmTxt="立即儲值"
          image="/images/ic-default-lock.png"
        />
      )}
    </div>
  );
};

export default ConsultPageComponent;
